import React, { useState } from 'react'
import { Button, Stack, Text } from '@thirstycamel/ui'

import { useActions } from '../../store/hooks'
import { useUser } from '../../hooks/useUser'

export interface StoreSelectFormProps {
  isSwitching?: boolean
  onComplete?: () => void
  onCancel?: () => void
}

export const StoreSelectForm = ({
  isSwitching = false,
  onComplete,
  onCancel,
}: StoreSelectFormProps) => {
  const { user, stores } = useUser()
  const selectStore = useActions(actions => actions.auth.selectStore)
  const [loadingStoreId, setLoadingStoreId] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const handleSelectStore = async (storeId: string) => {
    if (isSwitching && user?.store?.id === storeId) {
      onCancel?.()
      return
    }

    setLoadingStoreId(storeId)
    setError(null)

    const response = await selectStore(storeId)

    if (response) {
      onComplete?.()
    } else {
      setError(new Error('Unable to log into that store. Please try again.'))
    }

    setLoadingStoreId(null)
  }

  return (
    <Stack spacing={5}>
      <Text>
        {isSwitching
          ? 'Choose which store you would like to log into.'
          : 'Your account is linked to multiple stores. Choose which store to log into.'}
      </Text>

      <Stack spacing={3}>
        {stores.map(store => {
          const isCurrentStore = isSwitching && user?.store?.id === store.id

          return (
            <Stack
              key={store.id}
              isInline
              spacing={4}
              align="center"
              justify="space-between"
              p={4}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Stack spacing={1}>
                <Text fontWeight="semibold">{store.name}</Text>
                <Text fontSize="sm" color="gray.600">
                  {[store.region, store.postcode].filter(Boolean).join(' ')}
                </Text>
                {isCurrentStore && (
                  <Text fontSize="sm" color="green.600">
                    Current store
                  </Text>
                )}
              </Stack>

              <Button
                variantColor="pink"
                isRound
                rightIcon="chevron-right"
                isLoading={loadingStoreId === store.id}
                isDisabled={!!loadingStoreId}
                onClick={() => handleSelectStore(store.id)}
              >
                {isCurrentStore ? 'Continue' : 'Login'}
              </Button>
            </Stack>
          )
        })}
      </Stack>

      {error && (
        <Text color="red.500" fontSize="sm">
          {error.message}
        </Text>
      )}

      {isSwitching && onCancel && (
        <Button variant="ghost" variantColor="pink" isRound onClick={onCancel}>
          Cancel
        </Button>
      )}
    </Stack>
  )
}

export default StoreSelectForm
